/**
 * Entity registry for MBS Hukuk Admin.
 *
 * Each entity describes:
 *  - resource:    path segment under /api/v1/admin/<resource>/
 *  - label:       Turkish display name (singular friendly)
 *  - title:       Plural / page title
 *  - category:    Sidebar category key (Management / Legal / Finance / CRM)
 *  - icon:        lucide-react icon name
 *  - fields:      array of { name, label, type, required?, options?, resource?,
 *                            hidden?, readOnly?, fullWidth? }
 *  - schema:      { app, model } — Django app/model for full dynamic schema loading
 *                 EntityListPage / EntityForm uses this to auto-load the field
 *                 definitions, choices, FK relations and required flags from
 *                 the backend.
 *
 * Field types: 'text' | 'number' | 'email' | 'phone' | 'date' | 'datetime' |
 *              'boolean' | 'select' | 'textarea' | 'url' | 'fk'
 *
 * The form auto-merges DECLARED fields with fields DISCOVERED from the API
 * response, so the form is always 100% complete even when this schema is
 * incomplete.
 */
import {
    BASVURU_TURLERI,
    DOSYA_DURUMLARI,
    BASVURU_KONULARI,
    ARAC_TIPLERI,
    EVRAK_TURLERI,
    VEKALET_DURUMU,
    ARACI_ODEME_DURUM,
    ODEME_TURLERI,
    FATURA_ODEME_DURUM,
    ICRA_TAKIBI_DURUMLARI,
    ICRA_TAKIP_TURLERI,
    EVRAK_ONAY_DURUMU,
    VEKALET_KAYIT_DURUMU,
    SIGORTA_BASVURU_DURUM,
    DOSYA_TIPLERI,
} from "./choices";
 
export const CATEGORIES = [
    { key: "Yönetim", label: "Yönetim" },
    { key: "CRM", label: "CRM" },
    { key: "Filo", label: "Filo Yönetimi" },
    { key: "Hukuk", label: "Hukuk" },
    { key: "Finans", label: "Finans" },
    { key: "Sistem", label: "Sistem" },
    { key: "Yeni", label: "Yeni" },
];
 
/**
 * Explicit sidebar layout (mirrors Django admin app order). Allows the same
 * entity to appear under multiple categories — a deliberate convenience for
 * frequently-accessed modules (e.g. Tahkim and Dava in both Yönetim & Hukuk).
 *
 * Each item: { key (entity key), title?, icon? } — title/icon override the
 * entity defaults if provided. `key` must exist in ENTITIES.
 */
export const SIDEBAR_LAYOUT = [
    {
        key: "Yönetim",
        items: [
            { key: "sigorta-dosyalari" },
            {
                path: "/yeni/tahkim-merkezi",
                title: "Tahkim Merkezi",
                icon: "Scale",
            },
            { key: "tahkim-dosyalari", title: "Tahkim Veri Girişi" },
            { key: "dava-dosyalari" },
            { key: "evraklar" },
            { key: "deger-kaybi" },
        ],
    },
    {
        key: "CRM",
        items: [
            { key: "muvekkiller" },
            {
                path: "/yeni/araci-merkezi",
                title: "Aracılar Merkezi",
                icon: "Users",
            },
            { key: "aracilar" },
            { key: "araci-bildirimler" },
            {
                path: "/yeni/araci-merkezi?tab=gonder",
                title: "Aracı Bildirim Gönder",
                icon: "Send",
            },
            {
                path: "/yeni/iletisim-merkezi",
                title: "İletişim Merkezi",
                icon: "MessageCircle",
            },
            { key: "araci-odemeleri" },
            { key: "randevular" },
            { key: "gorusme-notlari" },
        ],
    },
    {
        key: "Filo",
        items: [
            {
                path: "/yeni/filo-merkezi",
                title: "Filo Merkezi",
                icon: "LayoutGrid",
            },
            {
                path: "/filo/canli-destek",
                title: "Filo Canlı Destek",
                icon: "Headset",
            },
            { key: "filolar" },
            { key: "filo-araclari" },
            { key: "suruculer" },
            { key: "hasar-kayitlari" },
            { key: "hasar-evraklari" },
            { key: "hasar-olaylari" },
            { key: "ekspertiz-kayitlari" },
            { key: "onarim-kayitlari" },
            { key: "karsi-taraflar" },
            { key: "tramer-sorgulari" },
            { key: "filo-arac-bakim" },
        ],
    },
    {
        key: "Hukuk",
        items: [
            {
                path: "/yeni/tahkim-merkezi",
                title: "Tahkim Merkezi",
                icon: "Scale",
            },
            { key: "tahkim-dosyalari", title: "Tahkim Kayıtları" },
            {
                path: "/yeni/icra-merkezi",
                title: "İcra Merkezi",
                icon: "Hammer",
            },
            { key: "icralar", title: "İcra Kayıtları" },
            { key: "dilekce-kayitlari" },
            { key: "vekaletler" },
            { key: "dava-dosyalari" },
        ],
    },
    {
        key: "Finans",
        items: [
            { key: "odemeler" },
            { key: "masraf-detaylari" },
            { key: "faturalar" },
            { key: "masraf-turleri" },
            { key: "anlasmali-servisler" },
        ],
    },
    {
        key: "Sistem",
        items: [
            {
                path: "/yeni/site-yonetimi",
                title: "Site Yönetimi",
                icon: "Globe",
            },
            {
                path: "/yeni/sistem-gunlugu",
                title: "Sistem Günlüğü",
                icon: "Activity",
            },
            { key: "audit-loglari", title: "Denetim İzleri" },
            { key: "hesap-hareketleri" },
            { key: "deger-kaybi-basvurulari" },
            { key: "pwa-kullanicilari" },
            { key: "push-bildirimler" },
            { key: "sigorta-basvurulari" },
            { key: "hatirlatmalar" },
            { key: "sigorta-dosyalari", title: "Sigorta Dosyaları" },
            { key: "sigorta-sirketleri" },
        ],
    },
];
 
const f = (name, label, type = "text", extra = {}) => ({
    name,
    label,
    type,
    ...extra,
});
 
/**
 * Wraps an entity config with Django app/model schema metadata so that
 * EntityListPage / EntityForm can auto-load the full dynamic schema from
 * the backend (fields, choices, FK relations, required flags).
 *
 * Usage:
 *   muvekkiller: withSchema({ ...config }, "musteri", "Muvekkil"),
 */
const withSchema = (config, app, model, options = {}) => ({
    ...config,
    schema: {
        app,
        model,
    },
    /** Yalnızca entities.js alanları; API şemasından ek alan gelmez */
    formFieldsStrict: options.formFieldsStrict !== false,
});
 
/* Common reusable fields */
const fieldMuvekkil = f("muvekkil", "Müvekkil", "fk", {
    resource: "muvekkiller",
});
const fieldSigortaSirketi = f("sigorta_sirketi", "Sigorta Şirketi", "fk", {
    resource: "sigorta-sirketleri",
});
const fieldAraci = f("araci", "Aracı", "fk", { resource: "aracilar" });
const fieldDosyaDurumu = f("dosya_durumu", "Durum", "select", {
    options: DOSYA_DURUMLARI,
});
const fieldBasvuruTuru = f("basvuru_turu", "Başvuru Türü", "select", {
    options: BASVURU_TURLERI,
});
const fieldBasvuruKonusu = f("basvuru_konusu", "Başvuru Konusu", "select", {
    options: BASVURU_KONULARI,
});
const fieldAracTipi = f("arac_tipi", "Araç Tipi", "select", {
    options: ARAC_TIPLERI,
});
const fieldVekaletDurumu = f("vekalet_durumu", "Vekalet Durumu", "select", {
    options: VEKALET_DURUMU,
});
 
export const ENTITIES = {
    /* ============================ MANAGEMENT ============================ */
    slider: withSchema({
        resource: "slider",
        label: "Slider",
        title: "Slider Yönetimi",
        category: "Management",
        icon: "Images",
        fields: [
            f("baslik", "Başlık", "text", { required: true }),
            f("alt_baslik", "Alt Başlık", "textarea"),
            f("link", "Link", "url"),
            f("siralama", "Sıralama", "number"),
            f("aktif", "Yayında", "boolean"),
        ],
    }, "musteri", "Slider", { formFieldsStrict: false }),
    "basvuru-formlari": withSchema({
        resource: "basvuru-formlari",
        label: "Başvuru Formu",
        title: "Başvuru Formları",
        category: "Management",
        icon: "FileSignature",
        fields: [
            f("ad_soyad", "Ad Soyad", "text", { required: true }),
            f("telefon", "Telefon", "phone", { required: true }),
            f("email", "E-posta", "email", { required: true }),
            f("kaza_tarihi", "Kaza Tarihi", "date"),
            f("mesaj", "Mesaj", "textarea", { fullWidth: true }),
            f("durum", "Yanıtlandı", "boolean"),
            f("tarih", "Başvuru Tarihi", "datetime", { readOnly: true }),
        ],
    }, "musteri", "BasvuruFormu", { formFieldsStrict: false }),
    hatirlatmalar: withSchema({
        resource: "hatirlatmalar",
        label: "Hatırlatma",
        title: "Hatırlatmalar",
        category: "Management",
        icon: "BellRing",
        fields: [
            f("baslik", "Başlık", "text", { required: true }),
            f("aciklama", "Açıklama", "textarea", { fullWidth: true }),
            f("hatirlatma_zamani", "Hatırlatma Zamanı", "datetime"),
            f("ofset_dk", "Ofset (dk)", "number", {
                placeholder: "Hedeften kaç dk önce/sonra",
            }),
            f("yontem", "Yöntem", "select", {
                options: [
                    { value: "sistem", label: "Panel Bildirim" },
                    { value: "eposta", label: "E-posta" },
                    { value: "sms", label: "SMS" },
                    { value: "whatsapp", label: "WhatsApp" },
                ],
            }),
            f("gonderildi_mi", "Gönderildi", "boolean"),
            f("gonderim_zamani", "Gönderim Zamanı", "datetime", {
                readOnly: true,
            }),
        ],
    }, "panel", "Hatirlatma"),
 
    /* ================================ LEGAL ============================== */
    "sigorta-dosyalari": withSchema({
        resource: "sigorta-dosyalari",
        label: "Sigorta Dosyası",
        title: "Sigorta Dosyaları",
        category: "Legal",
        icon: "ShieldCheck",
        fields: [
            // Group 1 — Müvekkil (en üstte)
            f("muvekkil", "Müvekkil", "fk", {
                resource: "muvekkiller",
                group: "Müvekkil Bilgileri",
                fullWidth: true,
                required: true,
            }),
            // Group 2 — Dosya Bilgileri
            f("dosya_no", "Dosya No", "text", {
                readOnly: true,
                group: "Dosya Bilgileri",
            }),
            f("kaza_tarihi", "Kaza Tarihi", "date", {
                group: "Dosya Bilgileri",
            }),
            f("kayit_tarihi", "Kayıt Tarihi", "datetime", {
                readOnly: true,
                group: "Dosya Bilgileri",
            }),
            f("dosya_durumu", "Dosya Durumu", "select", {
                options: DOSYA_DURUMLARI,
                group: "Dosya Bilgileri",
            }),
            f("dosya_tipi", "Dosya Tipi", "select", {
                options: DOSYA_TIPLERI,
                group: "Dosya Bilgileri",
            }),
            f("basvuru_konusu", "Başvuru Konusu", "select", {
                options: BASVURU_KONULARI,
                group: "Dosya Bilgileri",
            }),
            f("basvuru_turu", "Başvuru Türü", "select", {
                options: BASVURU_TURLERI,
                group: "Dosya Bilgileri",
            }),
            f("kusur_orani", "Kusur Oranı (%)", "select", {
                options: [
                    { value: "0", label: "%0" },
                    { value: "25", label: "%25" },
                    { value: "50", label: "%50" },
                    { value: "75", label: "%75" },
                    { value: "100", label: "%100" },
                ],
                numericSubmit: true,
                group: "Dosya Bilgileri",
            }),
            f("avukat", "Avukat", "fk", {
                resource: "kullanicilar",
                group: "Dosya Bilgileri",
            }),
            f("arac_rayic_degeri", "Araç Rayiç Değeri", "number", {
                group: "Dosya Bilgileri",
            }),
            f("yatar_acilacak_mi", "Yatar Açılacak mı?", "boolean", {
                group: "Dosya Bilgileri",
            }),
            f("referans_kodu", "Referans Kodu", "text", {
                group: "Dosya Bilgileri",
            }),
            f("filo", "Filo", "fk", {
                resource: "filolar",
                group: "Dosya Bilgileri",
            }),
            f("sure_uyarisi_olusturuldu", "Süre uyarısı oluşturuldu", "boolean", {
                readOnly: true,
                group: "Süre Takibi",
            }),
            f("basvuru_gonderim_tarihi", "Başvuru gönderim", "date", {
                group: "Başvuru → Ön Tahkim",
            }),
            f("basvuru_bekleme_gun", "SLA gün", "number", {
                readOnly: true,
                group: "Başvuru → Ön Tahkim",
            }),
            f("on_tahkim_hedef_tarihi", "Ön tahkim hedef", "date", {
                readOnly: true,
                group: "Başvuru → Ön Tahkim",
            }),
            f("on_tahkim_sure_durumu", "SLA durumu", "select", {
                readOnly: true,
                options: [
                    { value: "BEKLIYOR", label: "Bekliyor" },
                    { value: "YAKLASIYOR", label: "Yaklaşıyor" },
                    { value: "HAZIR", label: "Hazır" },
                    { value: "GECIKTI", label: "Gecikmiş" },
                    { value: "UYGULANDI", label: "Uygulandı" },
                ],
                group: "Başvuru → Ön Tahkim",
            }),
            f("on_tahkim_gecis_modu", "Geçiş modu", "select", {
                options: [
                    { value: "OTOMATIK", label: "Otomatik — SLA dolunca ön tahkim" },
                    { value: "MANUEL", label: "Manuel — ben onaylayacağım" },
                ],
                group: "Başvuru → Ön Tahkim",
            }),
            // Group 3 — Araç Bilgileri
            f("plaka", "Plaka", "text", { group: "Araç Bilgileri" }),
            f("marka", "Marka", "text", { group: "Araç Bilgileri" }),
            f("model", "Model", "text", { group: "Araç Bilgileri" }),
            f("model_yili", "Model Yılı", "number", {
                group: "Araç Bilgileri",
            }),
            f("km", "Kilometre", "number", { group: "Araç Bilgileri" }),
            f("sase", "Şase No", "text", { group: "Araç Bilgileri" }),
            f("motor_no", "Motor No", "text", { group: "Araç Bilgileri" }),
            f("arac_tipi", "Araç Tipi", "select", {
                options: ARAC_TIPLERI,
                group: "Araç Bilgileri",
            }),
            // Group 4 — Sigorta Bilgileri
            f("sigorta_sirketi", "Sigorta Şirketi", "fk", {
                resource: "sigorta-sirketleri",
                group: "Sigorta Bilgileri",
                fullWidth: true,
                fkSubmit: "label",
            }),
            f("eksik_evrak_mail_gonderildi", "Eksik evrak mail gönderildi", "boolean", {
                group: "Dosya Bilgileri",
            }),
            f("polis_no", "Poliçe No", "text", {
                group: "Sigorta Bilgileri",
            }),
            f("polis_baslangic_tarihi", "Poliçe Başlangıç", "date", {
                group: "Sigorta Bilgileri",
            }),
            f("polis_bitis_tarihi", "Poliçe Bitiş", "date", {
                group: "Sigorta Bilgileri",
            }),
            // Group 5 — Süre Takibi
            f("zamanasimi_yil", "Zamanaşımı (Yıl)", "number", {
                group: "Süre Takibi",
            }),
            f("uyari_baslangic_gun", "Uyarı Başlangıç (Gün)", "number", {
                group: "Süre Takibi",
            }),
            f("zamanasimi_son_tarihi", "Zamanaşımı Son Tarihi", "date", {
                readOnly: true,
                group: "Süre Takibi",
            }),
            f("kalan_gun", "Kalan Gün", "number", {
                readOnly: true,
                group: "Süre Takibi",
            }),
            f("sure_durumu", "Süre Durumu", "text", {
                readOnly: true,
                group: "Süre Takibi",
            }),
        ],
    }, "musteri", "SigortaDosyasi"),
    "tahkim-dosyalari": withSchema({
        resource: "tahkim-dosyalari",
        label: "Tahkim Dosyası",
        title: "Tahkim Dosyaları",
        category: "Legal",
        icon: "Scale",
        fields: [
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
                required: true,
            }),
            f("basvuru_no", "Başvuru No", "text"),
            f("basvuru_sahibi", "Başvuru Sahibi", "text"),
            f("sigorta_sirketi", "Sigorta Şirketi", "text"),
            f("hakem", "Hakem", "text"),
            f("hakem_email", "Hakem E-posta", "email"),
            f("bilirkisi_adi", "Bilirkişi Ad Soyad", "text"),
            f("bilirkişi_ucreti", "Bilirkişi Ücreti (TL)", "number"),
            f("son_odeme_tarihi", "Son Ödeme Tarihi", "date"),
            f("iban", "IBAN", "text"),
            f("karar_linki", "Ara Karar Linki", "url"),
            f("dogrulama_kodu", "Doğrulama Kodu", "text"),
            f("tahkim_asamasi", "Tahkim Aşaması", "select", {
                options: [
                    { value: "ON_TAHKIM", label: "Ön Tahkim" },
                    { value: "BASVURU", label: "Başvuru Yapıldı" },
                    { value: "HAKEM", label: "Hakem Atandı" },
                    { value: "BILIRKISI", label: "Bilirkişi" },
                    { value: "ARA_KARAR", label: "Ara Karar" },
                    { value: "KARAR", label: "Karar" },
                    { value: "ODEME", label: "Ödeme Bekleniyor" },
                    { value: "KAPANDI", label: "Kapandı" },
                ],
            }),
            f("basvuru_tarihi", "Başvuru Tarihi", "date"),
            f("hakem_atama_tarihi", "Hakem Atama", "date"),
            f("karar_tarihi", "Karar Tarihi", "date"),
            f("panel_notlar", "Tahkim Notları", "textarea", { fullWidth: true }),
            f("odeme_durumu", "Ödeme Durumu", "select", {
                options: [
                    { value: "BEKLIYOR", label: "Bekliyor" },
                    { value: "ODENDI", label: "Ödendi" },
                    { value: "GECIKTI", label: "Gecikti" },
                    { value: "IPTAL", label: "İptal" },
                ],
            }),
            f("odeme_tutari", "Ödenen Tutar", "number"),
            f("kapanis_evrak_tamam", "Kapanış evrak tamam", "checkbox"),
            f("kapanis_odeme_onay", "Kapanış ödeme onay", "checkbox"),


        ],
    }, "musteri", "TahkimDosyasi"),
    "dava-dosyalari": withSchema({
        resource: "dava-dosyalari",
        label: "Dava Dosyası",
        title: "Dava Dosyaları",
        category: "Legal",
        icon: "Gavel",
        fields: [
            f("davaci", "Davacı", "text", { required: true }),
            f("davali", "Davalı", "text", { required: true }),
            f("mahkeme_ve_esas", "Mahkeme - Esas", "text", {
                required: true,
            }),
            f("davanin_konusu", "Davanın Konusu", "text"),
            f("hangi_asamada", "Hangi Aşamada", "select", {
                options: [
                    { value: "ön inceleme", label: "Ön İnceleme" },
                    { value: "istinafta", label: "İstinafta" },
                    { value: "kapatıldı", label: "Kapatıldı" },
                ],
            }),
            f("notlar", "Not", "textarea", { fullWidth: true }),
            f("olusturma_tarihi", "Oluşturulma", "datetime", {
                readOnly: true,
            }),
        ],
    }, "musteri", "DavaDosyasi"),
    dosyalar: withSchema({
        resource: "sigorta-dosyalari",
        label: "Dosya",
        title: "Tüm Dosyalar",
        category: "Legal",
        icon: "FolderKanban",
        fields: [
            f("dosya_no", "Dosya No", "text", { required: true }),
            fieldMuvekkil,
            f("plaka", "Plaka", "text"),
            fieldBasvuruKonusu,
            fieldDosyaDurumu,
            f("kaza_tarihi", "Kaza Tarihi", "date"),
            f("kayit_tarihi", "Kayıt Tarihi", "datetime", { readOnly: true }),
        ],
    }, "musteri", "SigortaDosyasi"),
    evraklar: withSchema({
        resource: "evraklar",
        label: "Evrak",
        title: "Evraklar",
        category: "Legal",
        icon: "FileText",
        fields: [
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
                required: true,
            }),
            f("evrak_turu", "Evrak Türü", "select", {
                options: EVRAK_TURLERI,
                required: true,
            }),
            f("onay_durumu", "Onay Durumu", "select", {
                options: EVRAK_ONAY_DURUMU,
            }),
            f("red_nedeni", "Red Nedeni", "textarea"),
            f("yuklenme_tarihi", "Yüklenme Tarihi", "datetime", {
                readOnly: true,
            }),
        ],
    }, "musteri", "Evrak"),
    icralar: withSchema({
        resource: "icralar",
        label: "İcra",
        title: "İcra Dosyaları",
        category: "Legal",
        icon: "Hammer",
        fields: [
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
                required: true,
            }),
            f("takip_turu", "Takip Türü", "select", {
                options: ICRA_TAKIP_TURLERI,
            }),
            f("borc_tutari", "Borç Tutarı (TL)", "number", {
                required: true,
            }),
            f("icra_mudurlugu", "İcra Müdürlüğü", "text"),
            f("icra_dosya_no", "İcra Dosya No", "text"),
            f("dosya_durumu", "Dosya Durumu", "select", {
                options: ICRA_TAKIBI_DURUMLARI,
            }),
            f("yargilama_masrafi", "Yargılama Masrafı (TL)", "number"),
            f("icra_harci", "İcra Harcı (TL)", "number"),
            f("avukat_ucreti", "Avukat Ücreti (TL)", "number"),
            f("icra_vekalet_ucreti", "İcra Vekalet Ücreti (TL)", "number"),
            f("toplam_masraf", "Toplam Masraf (TL)", "number", {
                readOnly: true,
            }),
            f("kazanc", "Net Kazanç (TL)", "number", { readOnly: true }),
            f("odeme_tahsil_edildi", "Borç Tahsil Edildi", "boolean"),
            f("tahsil_edilen_tutar", "Tahsil Edilen (TL)", "number", {
                readOnly: true,
            }),
            f("son_tahsilat_tarihi", "Son Tahsilat", "date", { readOnly: true }),
            f("panel_notlar", "İcra Notları", "textarea", { fullWidth: true }),
            f("olusturulma_tarihi", "Oluşturulma", "datetime", {
                readOnly: true,
            }),
        ],
    }, "musteri", "IcraTakibi"),
    "deger-kaybi": withSchema({
        resource: "deger-kaybi",
        label: "Değer Kaybı",
        title: "Değer Kaybı Dosyaları",
        category: "Legal",
        icon: "TrendingDown",
        fields: [
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
                required: true,
                fullWidth: true,
            }),
            f("hesaplanan_baz_deger_kaybi", "Baz Değer Kaybı (TL)", "number", {
                readOnly: true,
            }),
            f("hesaplanan_real_deger_kaybi", "Gerçek Değer Kaybı (TL)", "number", {
                readOnly: true,
            }),
            f("olusturma_tarihi", "Oluşturma Tarihi", "datetime", {
                readOnly: true,
            }),
        ],
    }, "musteri", "DegerKaybi"),
    "dilekce-kayitlari": withSchema({
        resource: "dilekce-kayitlari",
        label: "Dilekçe Kaydı",
        title: "Dilekçe Kayıtları",
        category: "Legal",
        icon: "FileEdit",
        fields: [
            f("dosya", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
                required: true,
                fullWidth: true,
            }),
            f("tur", "Dilekçe Türü", "select", {
                options: [
                    { value: "sigorta", label: "Sigorta Başvuru" },
                    { value: "tahkim", label: "Tahkim Başvuru" },
                ],
                required: true,
            }),
            f("muvekkil_ad", "Müvekkil Ad", "text"),
            f("muvekkil_soyad", "Müvekkil Soyad", "text"),
            f("muvekkil_tc", "TC Kimlik", "text"),
            f("plaka", "Plaka", "text"),
            f("karsi_plaka", "Karşı Plaka", "text"),
            f("sigorta_sirketi", "Sigorta Şirketi", "text"),
            f("kaza_tarihi", "Kaza Tarihi", "date"),
            f("kusur_orani", "Kusur Oranı", "text"),
            f("aciklamalar", "Açıklamalar", "textarea", { fullWidth: true }),
            f("deliller", "Deliller", "textarea", { fullWidth: true }),
            f("talep_sonucu", "Talep Sonucu", "textarea", { fullWidth: true }),
            f("olusturma_tarihi", "Oluşturulma", "datetime", {
                readOnly: true,
            }),
        ],
    }, "dilekce", "DilekceKaydi"),
    vekaletler: withSchema({
        resource: "vekaletler",
        label: "Vekalet",
        title: "Vekaletler",
        category: "Legal",
        icon: "FileCheck",
        fields: [
            fieldMuvekkil,
            f("baslangic_tarihi", "Başlangıç Tarihi", "date"),
            f("bitis_tarihi", "Bitiş Tarihi", "date"),
            f("durum", "Durum", "select", {
                options: VEKALET_KAYIT_DURUMU,
            }),
            f("aciklama", "Açıklama", "textarea", { fullWidth: true }),
            f("olusturma_tarihi", "Oluşturulma", "datetime", {
                readOnly: true,
            }),
        ],
    }, "musteri", "Vekalet"),
    "sigorta-basvurulari": withSchema({
        resource: "sigorta-basvurulari",
        label: "Sigorta Başvurusu",
        title: "Sigorta Başvuruları",
        category: "Legal",
        icon: "ClipboardList",
        fields: [
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
                required: true,
            }),
            f("sigorta_sirketi", "Sigorta Şirketi", "fk", {
                resource: "sigorta-sirketleri",
                required: true,
            }),
            f("kusurlu_arac_plaka", "Kusurlu Araç Plaka", "text"),
            f("konu", "Konu", "text", { fullWidth: true }),
            f("alicilar", "Kime (alıcılar)", "textarea", {
                placeholder: "Şirket seçilince otomatik dolar — virgülle ayırın",
                fullWidth: true,
            }),
            f("sablon_html", "Mail İçeriği", "textarea", {
                fullWidth: true,
                rows: 14,
                mono: true,
                placeholder:
                    "Mail Hazırla ile alıcı, konu ve metin e-posta programına aktarılır (düz metin). Evrak/ZIP paketini siz ekleyin.",
            }),
            f("durum", "Durum", "select", {
                options: SIGORTA_BASVURU_DURUM,
            }),
            f("olusturma_tarihi", "Oluşturulma", "datetime", {
                readOnly: true,
            }),
        ],
    }, "sigorta", "SigortaBasvuru"),
 
    /* =============================== FINANCE ============================= */
    odemeler: withSchema({
        resource: "odemeler",
        label: "Ödeme Takibi",
        title: "Ödeme Takibi",
        category: "Finance",
        icon: "Wallet",
        fields: [
            {
                __groups: [
                    {
                        title: "Ödeme bilgileri",
                        fields: [
                            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                                resource: "sigorta-dosyalari",
                                required: true,
                                fullWidth: true,
                            }),
                            f("icra_takibi", "İcra Dosyası", "fk", {
                                resource: "icralar",
                                fullWidth: true,
                            }),
                            f("odeme_turu", "Ödeme Türü", "select", {
                                options: ODEME_TURLERI,
                            }),
                            f("odeme_miktari", "Ödeme Miktarı (TL)", "number"),
                            f("anlasma_orani", "Anlaşma Oranı (%)", "number"),
                            f("odeme_yapildi", "Ödeme Yapıldı", "boolean"),
                            f("whatsapp_gonder", "WhatsApp'tan mesaj gönder", "boolean", {
                                helpText:
                                    "İşaretlenirse kayıt sonrası müvekkile ödeme özeti mesajı ve PDF gönderilir. Müvekkilde WhatsApp onayı ve telefon gerekir.",
                            }),
                            f("kayit_tarihi", "Kayıt Tarihi", "datetime", {
                                readOnly: true,
                            }),
                        ],
                    },
                ],
            },
            f("toplam_masraf", "Toplam Masraf (TL)", "number", {
                readOnly: true,
                hidden: true,
            }),
            f("sigortali_hakedis", "Sigortalı Hakediş (TL)", "number", {
                readOnly: true,
                hidden: true,
            }),
            f("mbs_hakedis", "MBS Hakediş (TL)", "number", {
                readOnly: true,
                hidden: true,
            }),
        ],
    }, "musteri", "OdemeTakibi"),
    "masraf-detaylari": withSchema({
        resource: "masraf-detaylari",
        label: "Masraf Kalemi",
        title: "Masraf Kalemleri",
        category: "Finance",
        icon: "Receipt",
        fields: [
            f("odeme_takibi", "Ödeme Kaydı", "fk", {
                resource: "odemeler",
                required: true,
            }),
            f("masraf_turu", "Masraf Türü", "fk", {
                resource: "masraf-turleri",
                required: true,
            }),
            f("miktar", "Miktar (TL)", "number", { required: true }),
        ],
    }, "musteri", "MasrafDetay"),
    faturalar: withSchema({
        resource: "faturalar",
        label: "Fatura",
        title: "Faturalar",
        category: "Finance",
        icon: "Receipt",
        fields: [
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
                required: true,
            }),
            f("fatura_no", "Fatura No", "text", { readOnly: true }),
            f("fatura_tarihi", "Fatura Tarihi", "date"),
            f("tutar", "Tutar (Vergisiz)", "number", { required: true }),
            f("vergi_orani", "Vergi Oranı (%)", "number"),
            f("indirim_orani", "İndirim Oranı (%)", "number"),
            f("toplam_tutar", "Toplam Tutar", "number", { readOnly: true }),
            f("odeme_durumu", "Ödeme Durumu", "select", {
                options: FATURA_ODEME_DURUM,
            }),
            f("para_birimi", "Para Birimi", "select", {
                options: [
                    { value: "TRY", label: "TRY" },
                    { value: "USD", label: "USD" },
                    { value: "EUR", label: "EUR" },
                ],
            }),
            f("aciklama", "Açıklama", "textarea"),
        ],
    }, "musteri", "Fatura"),
    "masraf-turleri": withSchema({
        resource: "masraf-turleri",
        label: "Masraf Türü",
        title: "Masraf Türleri",
        category: "Finance",
        icon: "Tag",
        fields: [
            f("isim", "Masraf Türü", "text", { required: true }),
            f("aktif", "Aktif", "boolean"),
        ],
    }, "musteri", "MasrafTuru"),
 
    /* ================================= CRM =============================== */
    muvekkiller: withSchema({
        resource: "muvekkiller",
        label: "Müvekkil",
        title: "Müvekkiller",
        category: "CRM",
        icon: "Users",
        fields: [
            // Group 1 — Kişisel Bilgiler
            f("ad", "Ad", "text", {
                required: true,
                group: "Kişisel Bilgiler",
            }),
            f("soyad", "Soyad", "text", {
                required: true,
                group: "Kişisel Bilgiler",
            }),
            f("kimlik_tipi", "Kimlik Türü", "select", {
                required: true,
                group: "Kişisel Bilgiler",
                options: [
                    { value: "TC", label: "T.C. Kimlik No (Şahıs)" },
                    { value: "VERGI", label: "Vergi No (Tüzel / Şirket)" },
                ],
            }),
            f("tc_kimlik_no", "T.C. Kimlik No", "text", {
                required: true,
                group: "Kişisel Bilgiler",
                kimlikLinked: true,
            }),
            f("telefon", "Telefon", "phone", {
                group: "Kişisel Bilgiler",
            }),
            f("email", "E-posta", "email", {
                group: "Kişisel Bilgiler",
            }),
            f("whatsapp_onay", "WhatsApp Onayı", "boolean", {
                group: "Kişisel Bilgiler",
            }),
            f("iban", "IBAN", "text", { group: "Kişisel Bilgiler" }),
            f("adres", "Adres", "textarea", {
                group: "Kişisel Bilgiler",
                fullWidth: true,
            }),
            f("sifre", "Şifre", "text", {
                group: "Kişisel Bilgiler",
                placeholder: "Yeni şifre (boş bırakılırsa değişmez)",
            }),
            // Group 2 — Sözleşme & Vekalet
            fieldVekaletDurumu &&
                {
                    ...fieldVekaletDurumu,
                    group: "Sözleşme & Vekalet",
                },
            f("anlasma_orani", "Anlaşma Oranı (%)", "number", {
                group: "Sözleşme & Vekalet",
            }),
            // Group 3 — Aracı & Ödeme
            f("aracilar", "Aracılar", "fk", {
                resource: "aracilar",
                multi: true,
                group: "Aracı & Ödeme",
                fullWidth: true,
                placeholder: "Birden fazla aracı seçebilirsiniz",
            }),
            f("araciya_odenen_miktar", "Aracı Hakediş Tutarı (TL)", "number", {
                group: "Aracı & Ödeme",
            }),
            f("araci_hakedisi_odendi", "Aracı hakedişi ödendi", "boolean", {
                group: "Aracı & Ödeme",
                help: "İşaretlenirse aracı detayında ödendi olarak görünür.",
            }),
        ].filter(Boolean),
    }, "musteri", "Muvekkil"),
    aracilar: withSchema({
        resource: "aracilar",
        label: "Aracı",
        title: "Aracılar",
        category: "CRM",
        icon: "Handshake",
        fields: [
            // Group 1 — Aracı Bilgileri
            f("ad", "Ad", "text", {
                required: true,
                group: "Aracı Bilgileri",
            }),
            f("email", "E-posta", "email", {
                group: "Aracı Bilgileri",
            }),
            f("telefon", "Telefon", "phone", {
                group: "Aracı Bilgileri",
            }),
            f("iban", "IBAN", "text", {
                group: "Aracı Bilgileri",
                placeholder: "TR00 0000 0000 0000 0000 0000 00",
                help: "Hakediş ödemeleri için aracı banka hesabı",
            }),
            f("aktif", "Aktif", "boolean", {
                group: "Aracı Bilgileri",
            }),
            f("hak_edis_miktari", "Hak Ediş Miktarı", "number", {
                readOnly: true,
                group: "Aracı Bilgileri",
            }),
            // Group 2 — Şifre Ayarı
            f("sifre", "Şifre", "text", {
                group: "Şifre Ayarı",
                placeholder: "Yeni şifre (boş bırakılırsa değişmez)",
                fullWidth: true,
            }),
            // Group 3 — Zaman Bilgileri
            f("kayit_tarihi", "Kayıt Tarihi", "datetime", {
                readOnly: true,
                group: "Zaman Bilgileri",
            }),
            f("guncelleme_tarihi", "Güncelleme Tarihi", "datetime", {
                readOnly: true,
                group: "Zaman Bilgileri",
            }),
        ],
    }, "araci", "Araci"),
    "araci-bildirimler": withSchema({
        resource: "araci-bildirimler",
        label: "Aracı Bildirimi",
        title: "Aracı Bildirimleri",
        category: "CRM",
        icon: "BellRing",
        fields: [
            f("araci", "Aracı", "fk", {
                resource: "aracilar",
                required: true,
                group: "Bildirim",
            }),
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
                group: "Bildirim",
            }),
            f("baslik", "Başlık", "text", {
                required: true,
                group: "Bildirim",
            }),
            f("icerik", "İçerik", "textarea", {
                required: true,
                fullWidth: true,
                group: "Bildirim",
            }),
            f("okundu", "Okundu", "boolean", {
                group: "Durum",
            }),
            f("olusturma_tarihi", "Oluşturma", "datetime", {
                readOnly: true,
                group: "Durum",
            }),
        ],
    }, "araci", "AraciBildirim"),
    "araci-odemeleri": withSchema({
        resource: "araci-odemeleri",
        label: "Aracı Ödemesi",
        title: "Aracı Ödemeleri",
        category: "CRM",
        icon: "Receipt",
        fields: [
            f("araci", "Aracı", "fk", { resource: "aracilar", required: true }),
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
                required: true,
            }),
            f("tutar", "Tutar (TL)", "number", { required: true }),
            f("tarih", "Ödeme Tarihi", "datetime"),
            f("aciklama", "Açıklama", "text"),
            f("odeme_durumu", "Durum", "select", {
                options: ARACI_ODEME_DURUM,
            }),
        ],
    }, "araci", "AraciOdeme"),
    filolar: withSchema({
        resource: "filolar",
        label: "Filo",
        title: "Filolar",
        category: "Filo",
        icon: "Truck",
        fields: [
            f("firma_adi", "Firma Adı", "text", { required: true, group: "Firma" }),
            f("ticari_unvan", "Ticari Ünvan", "text", { group: "Firma" }),
            f("email", "Giriş E-postası", "email", { required: true, group: "İletişim" }),
            f("telefon", "Telefon", "phone", { group: "İletişim" }),
            f("vergi_no", "Vergi No", "text", { group: "Firma" }),
            f("vergi_dairesi", "Vergi Dairesi", "text", { group: "Firma" }),
            f("il", "İl", "text", { group: "Adres" }),
            f("ilce", "İlçe", "text", { group: "Adres" }),
            f("adres", "Adres", "textarea", { fullWidth: true, group: "Adres" }),
            f("yetkili_ad", "Yetkili Adı", "text", { group: "Yetkili" }),
            f("yetkili_tel", "Yetkili Telefon", "phone", { group: "Yetkili" }),
            f("yetkili_email", "Yetkili E-posta", "email", { group: "Yetkili" }),
            f("filo_tipi", "Filo Tipi", "select", {
                options: [
                    { value: "rentacar", label: "Rent A Car" },
                    { value: "lojistik", label: "Lojistik" },
                    { value: "kurumsal", label: "Kurumsal Filo" },
                    { value: "sigorta", label: "Sigorta Acentesi" },
                    { value: "diger", label: "Diğer" },
                ],
                group: "Firma",
            }),
            f("anlasma_orani", "Anlaşma Oranı (%)", "number", { group: "Firma" }),
            f("aktif", "Aktif", "boolean", { group: "Durum" }),
            f("erp_webhook_url", "ERP Webhook URL", "text", {
                group: "ERP Entegrasyon",
                placeholder: "https://erp.sirket.com/mbs-webhook",
                fullWidth: true,
            }),
            f("erp_webhook_secret", "ERP Webhook Secret", "text", {
                group: "ERP Entegrasyon",
                placeholder: "HMAC imza anahtarı (opsiyonel)",
            }),
            f("sifre", "Şifre", "text", {
                group: "Güvenlik",
                placeholder: "Yeni şifre (boş bırakılırsa değişmez)",
                fullWidth: true,
            }),
            f("olusturma_tarihi", "Kayıt Tarihi", "datetime", {
                readOnly: true,
                group: "Durum",
            }),
        ],
    }, "filo", "Filo"),
    "filo-araclari": withSchema({
        resource: "filo-araclari",
        label: "Filo Aracı",
        title: "Filo Araçları",
        category: "Filo",
        icon: "Car",
        fields: [
            f("filo", "Filo", "fk", { resource: "filolar", required: true, group: "Araç" }),
            f("plaka", "Plaka", "text", { required: true, group: "Araç" }),
            f("marka", "Marka", "text", { group: "Araç" }),
            f("model", "Model", "text", { group: "Araç" }),
            f("model_yili", "Model Yılı", "number", { group: "Araç" }),
            f("km", "Kilometre", "number", { group: "Araç" }),
            f("yakit", "Yakıt", "select", {
                options: [
                    { value: "BENZIN", label: "Benzin" },
                    { value: "DIZEL", label: "Dizel" },
                    { value: "LPG", label: "LPG" },
                    { value: "HIBRIT", label: "Hibrit" },
                    { value: "ELEKTRIK", label: "Elektrik" },
                ],
                group: "Teknik",
            }),
            f("vites", "Vites", "select", {
                options: [
                    { value: "MANUEL", label: "Manuel" },
                    { value: "OTOMATIK", label: "Otomatik" },
                ],
                group: "Teknik",
            }),
            f("durum", "Durum", "select", {
                options: [
                    { value: "aktif", label: "Aktif" },
                    { value: "servis", label: "Serviste" },
                    { value: "pert", label: "Pert" },
                    { value: "satildi", label: "Satıldı" },
                    { value: "beklemede", label: "Beklemede" },
                ],
                group: "Durum",
            }),
            f("sase_no", "Şase No", "text", { group: "Teknik" }),
            f("renk", "Renk", "text", { group: "Teknik" }),
        ],
    }, "filo", "FiloArac"),
    suruculer: withSchema({
        resource: "suruculer",
        label: "Sürücü",
        title: "Sürücüler",
        category: "Filo",
        icon: "Users",
        fields: [
            f("filo", "Filo", "fk", { resource: "filolar", required: true }),
            f("ad_soyad", "Ad Soyad", "text", { required: true }),
            f("tc_no", "T.C. Kimlik No", "text"),
            f("telefon", "Telefon", "phone", { required: true }),
            f("ehliyet_sinifi", "Ehliyet Sınıfı", "text"),
            f("ehliyet_tarihi", "Ehliyet Tarihi", "date"),
            f("aktif", "Aktif", "boolean"),
            f("eklenme_tarihi", "Kayıt Tarihi", "datetime", { readOnly: true }),
        ],
    }, "filo", "Surucu"),
    "hasar-kayitlari": withSchema({
        resource: "hasar-kayitlari",
        label: "Hasar Kaydı",
        title: "Hasar Kayıtları",
        category: "Filo",
        icon: "AlertTriangle",
        fields: [
            f("filo", "Filo", "fk", { resource: "filolar", required: true }),
            f("arac", "Araç", "fk", { resource: "filo-araclari", required: true }),
            f("surucu", "Sürücü", "fk", { resource: "suruculer" }),
            f("kaza_tarihi", "Kaza Tarihi", "datetime", { required: true }),
            f("kaza_konumu", "Kaza Konumu", "text"),
            f("hasar_tipi", "Hasar Tipi", "select", {
                options: [
                    { value: "TEK_TARAFLI", label: "Tek Taraflı" },
                    { value: "CIFT_TARAFLI", label: "Çift Taraflı" },
                    { value: "PARK_HALINDE", label: "Park Halinde" },
                    { value: "DOGAL_AFET", label: "Doğal Afet" },
                    { value: "DIGER", label: "Diğer" },
                ],
            }),
            f("durum", "Operasyon Durumu", "select", {
                options: [
                    { value: "BILDIRILDI", label: "İhbar Edildi" },
                    { value: "EKSPER_BEKLIYOR", label: "Eksper Bekleniyor" },
                    { value: "ONARIMDA", label: "Onarımda" },
                    { value: "TAMAMLANDI", label: "Tamamlandı" },
                    { value: "HUKUKTA", label: "Hukuki Süreç" },
                ],
            }),
            f("aciklama", "Açıklama", "textarea", { fullWidth: true }),
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
            }),
        ],
    }, "filo", "HasarKaydi"),
    "hasar-evraklari": withSchema({
        resource: "hasar-evraklari",
        label: "Hasar Evrakı",
        title: "Hasar Evrakları",
        category: "Filo",
        icon: "FileText",
        fields: [
            f("hasar_kaydi", "Hasar Kaydı", "fk", {
                resource: "hasar-kayitlari",
                required: true,
            }),
            f("evrak_tipi", "Evrak Tipi", "select", {
                options: [
                    { value: "KAZA_TUTANAGI", label: "Kaza Tespit Tutanağı" },
                    { value: "RUHSAT", label: "Araç Ruhsatı" },
                    { value: "EHLIYET", label: "Sürücü Ehliyeti" },
                    { value: "KAZA_FOTO", label: "Kaza Yeri Fotoğrafları" },
                    { value: "SIGORTA_POLICESI", label: "Sigorta Poliçesi" },
                    { value: "DIGER", label: "Diğer Belge" },
                ],
            }),
            f("aciklama", "Notlar", "text"),
            f("yuklenme_tarihi", "Yüklenme", "datetime", { readOnly: true }),
        ],
    }, "filo", "HasarEvrak"),
    "hasar-olaylari": withSchema({
        resource: "hasar-olaylari",
        label: "Hasar Olayı",
        title: "Hasar Olayları",
        category: "Filo",
        icon: "AlertTriangle",
        fields: [
            f("filo", "Filo", "fk", { resource: "filolar", required: true }),
            f("baslik", "Başlık", "text", { required: true }),
            f("olay_tarihi", "Olay Tarihi", "datetime", { required: true }),
            f("konum", "Konum", "text"),
            f("aciklama", "Açıklama", "textarea", { fullWidth: true }),
        ],
    }, "filo", "HasarOlayi"),
    "ekspertiz-kayitlari": withSchema({
        resource: "ekspertiz-kayitlari",
        label: "Ekspertiz",
        title: "Ekspertiz Kayıtları",
        category: "Filo",
        icon: "Scale",
        fields: [
            f("hasar_kaydi", "Hasar Kaydı", "fk", {
                resource: "hasar-kayitlari",
                required: true,
            }),
            f("eksper_adi", "Eksper", "text"),
            f("servis_adi", "Servis", "text"),
            f("randevu_tarihi", "Randevu", "datetime"),
            f("rapor_tarihi", "Rapor Tarihi", "datetime"),
            f("tahmini_tutar", "Tahmini Tutar", "number"),
            f("onaylanan_tutar", "Onaylanan Tutar", "number"),
            f("durum", "Durum", "select", {
                options: [
                    { value: "BEKLIYOR", label: "Randevu Bekleniyor" },
                    { value: "ATANDI", label: "Eksper Atandı" },
                    { value: "RAPORLANDI", label: "Rapor Alındı" },
                    { value: "IPTAL", label: "İptal" },
                ],
            }),
            f("notlar", "Notlar", "textarea", { fullWidth: true }),
        ],
    }, "filo", "EkspertizKaydi"),
    "onarim-kayitlari": withSchema({
        resource: "onarim-kayitlari",
        label: "Onarım",
        title: "Onarım Kayıtları",
        category: "Filo",
        icon: "Wrench",
        fields: [
            f("hasar_kaydi", "Hasar Kaydı", "fk", {
                resource: "hasar-kayitlari",
                required: true,
            }),
            f("servis_adi", "Servis", "text", { required: true }),
            f("baslangic_tarihi", "Başlangıç", "date"),
            f("bitis_tarihi", "Bitiş", "date"),
            f("tutar", "Tutar", "number"),
            f("durum", "Durum", "select", {
                options: [
                    { value: "BEKLIYOR", label: "Onarım Bekliyor" },
                    { value: "PARCA_BEKLIYOR", label: "Parça Bekleniyor" },
                    { value: "ONARIMDA", label: "Onarımda" },
                    { value: "TESLIM", label: "Teslim Edildi" },
                    { value: "IPTAL", label: "İptal" },
                ],
            }),
            f("notlar", "Notlar", "textarea"),
        ],
    }, "filo", "OnarimKaydi"),
    "karsi-taraflar": withSchema({
        resource: "karsi-taraflar",
        label: "Karşı Taraf",
        title: "Karşı Taraflar",
        category: "Filo",
        icon: "Users",
        fields: [
            f("hasar_kaydi", "Hasar Kaydı", "fk", { resource: "hasar-kayitlari" }),
            f("olay", "Olay", "fk", { resource: "hasar-olaylari" }),
            f("plaka", "Plaka", "text"),
            f("ad_soyad", "Ad Soyad", "text"),
            f("telefon", "Telefon", "phone"),
            f("sigorta_sirketi", "Sigorta Şirketi", "text"),
            f("police_no", "Poliçe No", "text"),
            f("kusur_orani", "Kusur %", "number"),
            f("notlar", "Notlar", "textarea"),
        ],
    }, "filo", "KarsiTaraf"),
    "tramer-sorgulari": withSchema({
        resource: "tramer-sorgulari",
        label: "İç Hasar Geçmişi",
        title: "İç Hasar Geçmişi Sorguları",
        category: "Filo",
        icon: "Shield",
        fields: [
            f("arac", "Araç", "fk", { resource: "filo-araclari", required: true }),
            f("sorgu_tarihi", "Sorgu Tarihi", "datetime", { readOnly: true }),
            f("toplam_hasar", "Toplam Hasar", "number"),
            f("kayit_sayisi", "Kayıt Sayısı", "number"),
            f("kaynak", "Kaynak", "select", {
                options: [
                    { value: "SISTEM", label: "Sistem Hesaplama" },
                    { value: "MANUEL", label: "Manuel Giriş" },
                    { value: "API", label: "Tramer API (gelecek)" },
                ],
                readOnly: true,
            }),
            f("notlar", "Notlar", "text"),
        ],
    }, "filo", "TramerSorgu"),
    "filo-arac-bakim": withSchema({
        resource: "filo-arac-bakim",
        label: "Araç Bakım",
        title: "Araç Bakım Takvimi",
        category: "Filo",
        icon: "Wrench",
        fields: [
            f("arac", "Araç", "fk", { resource: "filo-araclari", required: true }),
            f("bakim_tipi", "Bakım Tipi", "select", {
                options: [
                    { value: "PERIYODIK", label: "Periyodik Servis" },
                    { value: "MUAYENE", label: "Muayene" },
                    { value: "LASTIK", label: "Lastik" },
                    { value: "TRAFIK_SIGORTA", label: "Trafik Sigortası" },
                    { value: "KASKO", label: "Kasko" },
                    { value: "DIGER", label: "Diğer" },
                ],
            }),
            f("planlanan_tarih", "Planlanan Tarih", "date", { required: true }),
            f("yapilan_tarih", "Yapılan Tarih", "date"),
            f("km_hedef", "Km Hedef", "number"),
            f("tamamlandi", "Tamamlandı", "boolean"),
            f("aciklama", "Açıklama", "text"),
        ],
    }, "filo", "FiloAracBakim"),
    "sigorta-sirketleri": withSchema({
        resource: "sigorta-sirketleri",
        label: "Sigorta Şirketi",
        title: "Sigorta Şirketleri",
        category: "CRM",
        icon: "Building2",
        readOnlyCrud: true,
        fields: [
            f("ad", "Şirket Adı", "text", { readOnly: true }),
            f("email", "Hizmet E-postası", "email", { readOnly: true }),
            f("telefon", "Telefon", "phone", { readOnly: true }),
            f("mektup_adres", "Posta Adresi", "textarea", { readOnly: true }),
            f("evrak_talep", "Evrak Talep E-postaları", "textarea", {
                readOnly: true,
            }),
            f("dk_muracaat", "DK Başvuru E-postaları", "textarea", {
                readOnly: true,
            }),
        ],
    }, "sigorta", "SigortaSirketi"),
    "anlasmali-servisler": withSchema({
        resource: "anlasmali-servisler",
        label: "Anlaşmalı Servis",
        title: "Anlaşmalı Servisler",
        category: "CRM",
        icon: "Wrench",
        fields: [
            f("servis_ismi", "Servis Adı", "text", { required: true }),
            f("usta_ad_soyad", "Usta Ad Soyad", "text"),
            f("telefon", "Telefon", "phone"),
            f("adres", "Adres", "textarea", { fullWidth: true }),
            f("odenen_miktar", "Ödenen Miktar (TL)", "number"),
            f("iban", "IBAN", "text"),
            fieldMuvekkil,
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
            }),
            f("olusturulma_tarihi", "Oluşturulma", "datetime", {
                readOnly: true,
            }),
        ],
    }, "musteri", "AnlasmaliServis", { formFieldsStrict: false }),
    randevular: withSchema({
        resource: "randevular",
        label: "Randevu",
        title: "Randevular",
        category: "CRM",
        icon: "CalendarDays",
        fields: [
            f("baslik", "Başlık", "text", { required: true, fullWidth: true }),
            fieldMuvekkil,
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
            }),
            f("baslangic", "Başlangıç", "datetime", { required: true }),
            f("bitis", "Bitiş", "datetime", { required: true }),
            f("tip", "Tip", "select", {
                options: [
                    { value: "gorusme", label: "Yüz yüze" },
                    { value: "telefon", label: "Telefon" },
                    { value: "online", label: "Online" },
                    { value: "diger", label: "Diğer" },
                ],
            }),
            f("onem", "Önem", "select", {
                options: [
                    { value: "dusuk", label: "Düşük" },
                    { value: "orta", label: "Orta" },
                    { value: "yuksek", label: "Yüksek" },
                ],
            }),
            f("durum", "Durum", "select", {
                options: [
                    { value: "planlandi", label: "Planlandı" },
                    { value: "tamamlandi", label: "Tamamlandı" },
                    { value: "iptal", label: "İptal" },
                ],
            }),
            f("konum", "Konum", "text"),
            f("tum_gun", "Tüm gün", "boolean"),
            f("aciklama", "Açıklama", "textarea", { fullWidth: true }),
        ],
    }, "panel", "Randevu", { formFieldsStrict: true }),
    "gorusme-notlari": withSchema({
        resource: "gorusme-notlari",
        label: "Görüşme Notu",
        title: "Görüşme Notları",
        category: "CRM",
        icon: "NotebookPen",
        fields: [
            f("konu", "Konu", "text", { required: true, fullWidth: true }),
            fieldMuvekkil,
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
            }),
            f("gorusme_zamani", "Görüşme Zamanı", "datetime", {
                required: true,
            }),
            f("kanal", "Kanal", "select", {
                options: [
                    { value: "yuz_yuze", label: "Yüz yüze" },
                    { value: "telefon", label: "Telefon" },
                    { value: "eposta", label: "E-posta" },
                    { value: "whatsapp", label: "WhatsApp" },
                    { value: "online", label: "Online" },
                ],
            }),
            f("ozet", "Özet", "textarea", { fullWidth: true }),
            f("detay", "Detay", "textarea", { fullWidth: true }),
            f("sonraki_aksiyon", "Sonraki Aksiyon", "text"),
            f("sonraki_aksiyon_tarihi", "Sonraki Aksiyon Tarihi", "datetime"),
        ],
    }, "panel", "GorusmeNotu"),
 
    /* ================================ SİSTEM ============================== */
    "deger-kaybi-basvurulari": withSchema({
        resource: "sigorta-basvurulari",
        label: "Değer Kaybı Başvurusu",
        title: "Değer Kaybı Başvuruları",
        category: "Sistem",
        icon: "ClipboardList",
        fields: [
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
                required: true,
            }),
            f("sigorta_sirketi", "Sigorta Şirketi", "fk", {
                resource: "sigorta-sirketleri",
                required: true,
            }),
            f("kusurlu_arac_plaka", "Kusurlu Araç Plaka", "text"),
            f("konu", "Konu", "text", { fullWidth: true }),
            f("alicilar", "Alıcılar (E-posta)", "textarea", { fullWidth: true }),
            f("durum", "Durum", "select", {
                options: [
                    { value: "taslak", label: "Taslak" },
                    { value: "hazir", label: "Hazır" },
                    { value: "gonderildi", label: "Gönderildi" },
                ],
            }),
            f("olusturma_tarihi", "Oluşturulma", "datetime", {
                readOnly: true,
            }),
        ],
    }, "sigorta", "SigortaBasvuru"),
    "pwa-kullanicilari": withSchema({
        resource: "pwa-kullanicilari",
        label: "PWA Kullanıcı",
        title: "PWA Kullanıcı Takibi",
        category: "Sistem",
        icon: "Users",
        fields: [
            f("ip_adresi", "IP Adresi", "text", { required: true }),
            f("tarayici", "Tarayıcı", "text"),
            f("cihaz", "Cihaz", "text"),
            f("pwa_kurulu_mu", "PWA Kurulu", "boolean"),
            f("push_izin", "Push İzni", "boolean"),
            f("versiyon", "Versiyon", "text"),
        ],
    }, "musteri", "PwaKullaniciTakip"),
    "push-bildirimler": withSchema({
        resource: "push-bildirimler",
        label: "Push Bildirim",
        title: "Push Bildirim",
        category: "Sistem",
        icon: "BellRing",
        fields: [
            f("baslik", "Başlık", "text", { required: true }),
            f("mesaj", "Mesaj", "textarea", { fullWidth: true }),
            f("basarili", "Başarılı", "boolean"),
            f("gonderim_tarihi", "Gönderim Tarihi", "datetime", {
                readOnly: true,
            }),
        ],
    }, "musteri", "PushBildirim"),

    "dosya-yorumlari": withSchema({
        resource: "dosya-yorumlari",
        label: "Dosya Yorumu",
        title: "Dosya Yorumları",
        category: "Yeni",
        icon: "MessageSquare",
        fields: [
            f("sigorta_dosyasi", "Sigorta Dosyası", "fk", {
                resource: "sigorta-dosyalari",
                required: true,
            }),
            f("metin", "Yorum", "textarea", { fullWidth: true, required: true }),
            f("kullanici", "Kullanıcı", "fk", { resource: "kullanicilar", readOnly: true }),
            f("olusturma_tarihi", "Tarih", "datetime", { readOnly: true }),
        ],
    }, "musteri", "DosyaYorumu"),

    "hesap-defterleri": withSchema({
        resource: "hesap-defterleri",
        label: "Hesap Defteri",
        title: "Hesap Defterleri",
        category: "Yeni",
        icon: "NotebookPen",
        fields: [
            f("ad", "Hesap Adı", "text", { required: true }),
            f("para_birimi", "Para Birimi", "select", {
                options: [
                    { value: "TRY", label: "₺ TL" },
                    { value: "EUR", label: "€ EUR" },
                    { value: "USD", label: "$ USD" },
                ],
            }),
            f("varsayilan", "Varsayılan Ödeme Hesabı", "boolean"),
            f("aktif", "Aktif", "boolean"),
            f("sira", "Sıra", "number"),
            f("aciklama", "Açıklama", "textarea", { fullWidth: true }),
        ],
    }, "musteri", "HesapDefteri"),

    "hesap-hareketleri": withSchema({
        resource: "hesap-hareketleri",
        label: "Hesap Hareketi",
        title: "Hesap Hareketleri",
        category: "Sistem",
        icon: "Receipt",
        fields: [
            f("hesap", "Hesap", "fk", { resource: "hesap-defterleri", required: true }),
            f("tutar", "Tutar", "number", { required: true }),
            f("hareket_turu", "Tür", "text", { readOnly: true }),
            f("aciklama", "Açıklama", "text"),
            f("tarih", "Tarih", "datetime", { readOnly: true }),
        ],
    }, "musteri", "HesapDefteriHareketi"),

    "yetki-sablonlari": withSchema({
        resource: "yetki-sablonlari",
        label: "Yetki Şablonu",
        title: "Yetki Şablonları",
        category: "Yeni",
        icon: "ShieldCheck",
        fields: [
            f("ad", "Şablon Adı", "text", { required: true }),
            f("aciklama", "Açıklama", "textarea", { fullWidth: true }),
            f("varsayilan", "Varsayılan", "boolean"),
        ],
    }, "musteri", "YetkiSablonu"),

    "audit-loglari": withSchema({
        resource: "audit-loglari",
        label: "Denetim İzi",
        title: "Sistem Denetim İzleri",
        category: "Sistem",
        icon: "History",
        readOnlyCrud: true,
        fields: [
            f("kullanici_label", "Kullanıcı", "text", { readOnly: true }),
            f("model_adi", "Model", "text", { readOnly: true }),
            f("nesne_id", "Nesne ID", "number", { readOnly: true }),
            f("nesne_temsili", "Nesne Temsili", "text", { readOnly: true }),
            f("islem_tipi_display", "İşlem", "text", { readOnly: true }),
            f("degisen_alanlar", "Değişen Alanlar", "json", { readOnly: true, fullWidth: true }),
            f("ip_adresi", "IP Adresi", "text", { readOnly: true }),
            f("olusturma_tarihi", "Zaman", "datetime", { readOnly: true }),
        ],
    }, "musteri", "SistemAuditLog"),

    haberler: withSchema({
        resource: "haberler",
        label: "Haber",
        title: "Haberler",
        category: "Sistem",
        icon: "FileText",
        fields: [
            f("baslik", "Başlık", "text", { required: true }),
            f("icerik", "İçerik", "textarea", { fullWidth: true, required: true }),
            f("kategori", "Kategori", "text"),
            f("etiketler", "Etiketler", "text"),
            f("yayin_tarihi", "Yayın Tarihi", "datetime"),
            f("aktif", "Yayında", "boolean"),
        ],
    }, "musteri", "Haber", { formFieldsStrict: false }),

    "whatsapp-loglari": withSchema({
        resource: "whatsapp-loglari",
        label: "WA Log",
        title: "WhatsApp Mesaj Logları",
        category: "Sistem",
        icon: "MessageSquare",
        readOnlyCrud: true,
        fields: [
            f("telefon", "Telefon", "phone", { readOnly: true }),
            f("mesaj", "Mesaj", "textarea", { readOnly: true, fullWidth: true }),
            f("yon", "Yön", "text", { readOnly: true }),
            f("dosya", "Dosya", "fk", { resource: "sigorta-dosyalari", readOnly: true }),
            f("olusturma_tarihi", "Tarih", "datetime", { readOnly: true }),
        ],
    }, "whatsapp", "WhatsAppMesaj"),

    "ai-gorevler": withSchema({
        resource: "ai-gorevler",
        label: "AI Görev",
        title: "AI Görevleri",
        category: "Sistem",
        icon: "Bot",
        readOnlyCrud: true,
        fields: [
            f("baslik", "Başlık", "text", { readOnly: true }),
            f("hedef", "Hedef", "text", { readOnly: true }),
            f("durum", "Durum", "text", { readOnly: true }),
            f("aciklama", "Açıklama", "textarea", { readOnly: true, fullWidth: true }),
            f("olusturma_tarihi", "Oluşturma", "datetime", { readOnly: true }),
        ],
    }, "akil", "AIGorev"),

    "ai-oneryler": withSchema({
        resource: "ai-oneryler",
        label: "AI Öneri",
        title: "AI Önerileri",
        category: "Sistem",
        icon: "Sparkles",
        readOnlyCrud: true,
        fields: [
            f("gorev", "Görev", "fk", { resource: "ai-gorevler", readOnly: true }),
            f("patch", "Öneri / Patch", "textarea", { readOnly: true, fullWidth: true }),
            f("commit_message", "Commit Mesajı", "text", { readOnly: true }),
            f("uygulandi", "Uygulandı", "boolean", { readOnly: true }),
            f("olusturma_tarihi", "Tarih", "datetime", { readOnly: true }),
        ],
    }, "akil", "AISuggestion"),
};
 
export function listEntities() {
    return Object.entries(ENTITIES).map(([key, e]) => ({ key, ...e }));
}
 
export function getEntity(key) {
    return ENTITIES[key] || null;
}

/** EntityDetailPage ve liste için alias */
export function resolveEntity(key) {
    return getEntity(key);
}
 
export function entitiesByCategory() {
    const grouped = {};
    for (const cat of CATEGORIES) grouped[cat.key] = [];
    for (const [key, e] of Object.entries(ENTITIES)) {
        if (!grouped[e.category]) grouped[e.category] = [];
        grouped[e.category].push({ key, ...e });
    }
    return grouped;
}